import React, { useRef, useEffect, useState } from 'react';
import { Location } from '../types';
import { geocodeAddress, reverseGeocode } from '../services/routexApi';

interface LocationInputProps {
  value: string;
  onChange: (value: string) => void;
  onLocationSelect: (location: Location, address: string) => void;
  placeholder: string;
  icon: string;
  iconColor: string;
  isStart?: boolean;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export const LocationInput: React.FC<LocationInputProps> = ({
  value,
  onChange,
  onLocationSelect,
  placeholder,
  icon,
  iconColor,
  isStart: _isStart = false,
  disabled = false,
  onFocus,
  onBlur,
  inputRef: externalInputRef,
}) => {
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef || internalInputRef;
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState(value);
  const isSelectingFromAutocomplete = useRef(false);
  const blurTimeoutRef = useRef<number | null>(null);
  const isGeocodingRef = useRef(false);
  const isClickingAutocompleteRef = useRef(false);
  const selectedAddressRef = useRef<string | null>(null);
  const autocompleteFallbackTimeoutRef = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);
  const onLocationSelectRef = useRef(onLocationSelect);
  const onBlurRef = useRef(onBlur);
  const valueRef = useRef(value);

  useEffect(() => {
    onChangeRef.current = onChange;
    onLocationSelectRef.current = onLocationSelect;
    onBlurRef.current = onBlur;
    valueRef.current = value;
  }, [onChange, onLocationSelect, onBlur, value]);

  // Sync input value when value prop changes from outside (but not during autocomplete selection or typing)
  useEffect(() => {
    if (isSelectingFromAutocomplete.current) return;
    const preferredValue =
      selectedAddressRef.current && value.trim() && selectedAddressRef.current.startsWith(value.trim())
        ? selectedAddressRef.current
        : value;
    setDisplayValue(preferredValue);
  }, [value]);

  useEffect(() => {
    if (!inputRef.current || !window.google?.maps?.places || autocompleteRef.current) return;

    // Create Autocomplete instance
    const autocomplete = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        fields: [
          'formatted_address',
          'address_components',
          'geometry',
          'name',
          'vicinity',
          'place_id'
        ],
        types: ['geocode', 'establishment'],
      }
    );

    autocompleteRef.current = autocomplete;

    // Listen for clicks on autocomplete dropdown items
    // This helps detect when user is clicking on a suggestion
    const scheduleAutocompleteFallbackConfirm = () => {
      if (autocompleteFallbackTimeoutRef.current) {
        clearTimeout(autocompleteFallbackTimeoutRef.current);
      }
      autocompleteFallbackTimeoutRef.current = window.setTimeout(() => {
        autocompleteFallbackTimeoutRef.current = null;
        if (!isClickingAutocompleteRef.current) return;
        if (isSelectingFromAutocomplete.current) return;
        if (handleGeocodeAddress.current) {
          handleGeocodeAddress.current();
        }
        isClickingAutocompleteRef.current = false;
      }, 180);
    };

    const handleDocumentPointerDown = (e: Event) => {
      const target = e.target as HTMLElement;
      // Check if click is on an autocomplete suggestion (Google Maps adds specific classes)
      if (
        target &&
        (target.classList.contains('pac-item') ||
          target.closest('.pac-item') ||
          target.classList.contains('pac-container') ||
          target.closest('.pac-container'))
      ) {
        isClickingAutocompleteRef.current = true;
        scheduleAutocompleteFallbackConfirm();
      }
    };

    // Detect mouse/touch selection on autocomplete suggestions.
    document.addEventListener('pointerdown', handleDocumentPointerDown, true);
    document.addEventListener('mousedown', handleDocumentPointerDown, true);
    document.addEventListener('touchstart', handleDocumentPointerDown, true);

    // Handle place selection (works for both keyboard and mouse)
    const handlePlaceSelect = () => {
      // Cancel any pending blur
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      // Set flag to prevent React from overriding the value and prevent geocoding
      isSelectingFromAutocomplete.current = true;

      const place = autocomplete.getPlace();

      // Check if place was actually selected (has place_id or geometry)
      if (!place.place_id && !place.geometry?.location) {
        // Place selection incomplete, reset flag
        isSelectingFromAutocomplete.current = false;
        isClickingAutocompleteRef.current = false;
        return;
      }

      if (!place.geometry?.location) {
        console.warn('Place selection missing geometry', place);
        isSelectingFromAutocomplete.current = false;
        isClickingAutocompleteRef.current = false;
        return;
      }

      const location: Location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      // Get the full formatted address - prioritize formatted_address
      let address = '';

      // First priority: formatted_address (full address)
      if (place.formatted_address) {
        address = place.formatted_address;
      }
      // Second priority: name + vicinity for establishments
      else if (place.name && place.vicinity) {
        address = `${place.name}, ${place.vicinity}`;
      }
      // Third priority: just name
      else if (place.name) {
        address = place.name;
      }
      // Fourth priority: check what Google set in the input field
      else if (inputRef.current?.value) {
        address = inputRef.current.value;
      }
      // Last resort: current prop value
      else {
        address = valueRef.current || '';
      }

      // Cancel any pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      // Immediately set the input value first (before React state update)
      // This ensures the input shows the selected address immediately
      selectedAddressRef.current = address;
      setDisplayValue(address);

      // Update parent state - this will update the value prop
      onChangeRef.current(address);

      // Clicking/tapping a suggestion is treated as final selection immediately.
      try {
        onLocationSelectRef.current(location, address);
      } catch (error) {
        console.error('Error calling onLocationSelect:', error);
      }

      // Reset flags immediately
      isSelectingFromAutocomplete.current = false;
      isClickingAutocompleteRef.current = false;
      if (autocompleteFallbackTimeoutRef.current) {
        clearTimeout(autocompleteFallbackTimeoutRef.current);
        autocompleteFallbackTimeoutRef.current = null;
      }

      // Immediately blur the input to complete the selection (like pressing Enter)
      // Don't wait for requestAnimationFrame - do it synchronously
      setIsFocused(false);
      if (inputRef.current) {
        // Use setTimeout with 0 delay to ensure it happens after the current call stack
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.blur();
          }
          onBlurRef.current?.();
        }, 0);
      } else {
        onBlurRef.current?.();
      }
    };

    // Listen for place_changed event (fired when place is selected via keyboard or mouse)
    const listener = autocomplete.addListener('place_changed', handlePlaceSelect);

    return () => {
      // Remove autocomplete listener
      if (listener) {
        window.google.maps.event.removeListener(listener);
      }

      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }

      document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
      document.removeEventListener('mousedown', handleDocumentPointerDown, true);
      document.removeEventListener('touchstart', handleDocumentPointerDown, true);

      // Clear any pending timeouts
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
      if (autocompleteFallbackTimeoutRef.current) {
        clearTimeout(autocompleteFallbackTimeoutRef.current);
        autocompleteFallbackTimeoutRef.current = null;
      }
    };
  });

  // Handle geocoding when user presses Enter without selecting from autocomplete
  const handleGeocodeAddress = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!window.google?.maps?.Geocoder) return;

    handleGeocodeAddress.current = () => {
      // Don't geocode if already processing or if selecting from autocomplete
      if (isGeocodingRef.current || isSelectingFromAutocomplete.current) {
        return;
      }

      const address = inputRef.current?.value?.trim();
      if (!address || address.length === 0) {
        return;
      }

      isGeocodingRef.current = true;

      geocodeAddress(address)
        .then((result) => {
          const location: Location = { lat: result.lat, lng: result.lng };
          const formattedAddress = result.formatted_address || address;
          selectedAddressRef.current = formattedAddress;
          setDisplayValue(formattedAddress);
          onChange(formattedAddress);
          onLocationSelect(location, formattedAddress);
          setTimeout(() => {
            setIsFocused(false);
            if (inputRef.current) inputRef.current.blur();
            onBlur?.();
          }, 100);
        })
        .catch(() => {
          selectedAddressRef.current = null;
        })
        .finally(() => {
          isGeocodingRef.current = false;
        });
    };
  }, [onChange, onLocationSelect, onBlur]);

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const location: Location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        // Reverse geocode to get address
        const fallbackAddress = `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
        reverseGeocode(location.lat, location.lng)
          .then((result) => {
            const address = result.formatted_address ?? fallbackAddress;
            onChange(address);
            onLocationSelect(location, address);
          })
          .catch(() => {
            selectedAddressRef.current = fallbackAddress;
            setDisplayValue(fallbackAddress);
            onChange(fallbackAddress);
            onLocationSelect(location, fallbackAddress);
          });
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to retrieve your location');
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000,
      }
    );
  };

  return (
    <div className="relative">
      <div
        className={`flex items-center bg-white rounded-lg border transition-all ${
          isFocused ? 'border-blue-500 shadow-md' : 'border-gray-200'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {/* Icon */}
        <div className="flex items-center pl-3 pr-2">
          <span
            className={`material-symbols-outlined text-[20px]`}
            style={{ color: iconColor }}
          >
            {icon}
          </span>
        </div>

        {/* Input - controlled input that works with Google Autocomplete */}
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => {
            // If disabled, prevent changes
            if (disabled) {
              e.preventDefault();
              return;
            }
            // Allow typing to work normally, but not during autocomplete selection
            if (!isSelectingFromAutocomplete.current) {
              selectedAddressRef.current = null;
              setDisplayValue(e.target.value);
              onChange(e.target.value);
            }
          }}
          onKeyDown={(e) => {
            // If disabled, prevent all keyboard input
            if (disabled) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            // Handle Enter key
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();

              // Check if autocomplete has a place selected
              const place = autocompleteRef.current?.getPlace();
              const hasPlaceSelected = place && (place.place_id || place.geometry?.location);

              // If autocomplete has a place selected, let handlePlaceSelect handle it
              // Otherwise, geocode the typed address
              if (!hasPlaceSelected && handleGeocodeAddress.current && !isSelectingFromAutocomplete.current) {
                handleGeocodeAddress.current();
              }
            }
            // Stop propagation for all keys to prevent interference with other forms
            e.stopPropagation();
          }}
          onKeyPress={(e) => {
            // If disabled, prevent all keyboard input
            if (disabled) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onKeyUp={(e) => {
            // If disabled, prevent all keyboard input
            if (disabled) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onFocus={() => {
            // If disabled, prevent focus
            if (disabled) {
              if (inputRef.current) {
                inputRef.current.blur();
              }
              return;
            }
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={(_e) => {
            // Delay blur processing to allow autocomplete selection to complete
            // This is especially important for mouse/touch selection
            // When clicking on autocomplete predictions, blur fires before place_changed
            // Use a longer delay when clicking to ensure place_changed fires first
            const delay = isClickingAutocompleteRef.current ? 1200 : 300;
            blurTimeoutRef.current = window.setTimeout(() => {
              // Only process blur if we're not in the middle of selecting from autocomplete
              if (!isSelectingFromAutocomplete.current && !isClickingAutocompleteRef.current) {
                setIsFocused(false);
                onBlurRef.current?.();
              }
            }, delay);
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 py-3 pr-2 text-gray-900 placeholder-gray-500 bg-transparent border-none outline-none text-[15px]"
          autoComplete="off"
          data-form-type="location"
        />

        {/* Current location button */}
        <button
          type="button"
          onClick={handleCurrentLocation}
          disabled={disabled}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed mr-1"
          title="Use current location"
        >
          <span className="material-symbols-outlined text-blue-600 text-[20px]">
            my_location
          </span>
        </button>
      </div>
    </div>
  );
};
